import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/features/auth/store'
import {
  ADJUSTMENT_REASONS,
  ADJUSTMENT_REASON_LABELS,
  ADJUSTMENT_STATUS_LABELS,
  approveAdjustment,
  formatDelta,
  formatMoney,
  listAdjustments,
  listAllProducts,
  listBalances,
  listLocations,
  rejectAdjustment,
  requestAdjustment,
  type Adjustment,
  type AdjustmentReason,
  type AdjustmentStatus,
} from '@/features/inventory/api'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const STATUS_TONE: Record<AdjustmentStatus, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  approved: 'bg-success/10 text-success',
  rejected: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
}

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })

interface Draft {
  product_id: string
  location_id: string
  qty_delta: string
  reason: AdjustmentReason
  notes: string
}

const EMPTY: Draft = {
  product_id: '',
  location_id: '',
  qty_delta: '',
  reason: 'adjustment',
  notes: '',
}

/**
 * Raise a correction.
 *
 * The quantity is the change, not the new total: `-12` writes off twelve, `+12` finds
 * twelve. A "counted" field would be friendlier to type and wrong for the same reason the
 * ledger is signed — the shelf may move between the count and the save, and a delta
 * applies correctly to whatever is there while a total silently overwrites it.
 */
function RaiseDialog({
  open,
  onClose,
  initialProductId,
}: {
  open: boolean
  onClose: () => void
  initialProductId: string
}) {
  const [draft, setDraft] = useState<Draft>({ ...EMPTY, product_id: initialProductId })
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const products = useQuery({
    queryKey: ['products', 'all'],
    queryFn: () => listAllProducts(),
    retry: false,
    staleTime: 5 * 60_000,
    enabled: open,
  })
  const locations = useQuery({
    queryKey: ['stock-locations'],
    queryFn: listLocations,
    retry: false,
    staleTime: 5 * 60_000,
    enabled: open,
  })
  // What is on the shelf right now, so the person typing -40 can see there are 12.
  const balance = useQuery({
    queryKey: ['balances', { product_id: draft.product_id, location_id: draft.location_id }],
    queryFn: () => listBalances({ product_id: draft.product_id, location_id: draft.location_id }),
    retry: false,
    enabled: open && Boolean(draft.product_id && draft.location_id),
  })

  const onHand = balance.data?.data[0]?.quantity ?? null

  const raise = useMutation({
    mutationFn: () =>
      requestAdjustment({
        product_id: draft.product_id,
        location_id: draft.location_id,
        qty_delta: Number(draft.qty_delta),
        reason: draft.reason,
        notes: draft.notes || null,
      }),
    onSuccess: (adjustment) => {
      void queryClient.invalidateQueries({ queryKey: ['adjustments'] })
      void queryClient.invalidateQueries({ queryKey: ['balances'] })
      void queryClient.invalidateQueries({ queryKey: ['movements'] })
      void queryClient.invalidateQueries({ queryKey: ['valuation'] })
      // Two different outcomes behind one 201, and the difference matters to whoever is
      // standing at the shelf: below the limit the stock has already moved, above it
      // nothing has and somebody else has to agree first.
      if (adjustment.status === 'pending') {
        toast.success('Sent for approval. Nothing moves until somebody else signs it off.')
      } else {
        toast.success('Stock corrected.')
      }
      setDraft({ ...EMPTY })
      onClose()
    },
    onError: (mutationError: unknown) => {
      setError(
        mutationError instanceof ApiError
          ? mutationError.message
          : 'Could not reach the server. Nothing was changed.',
      )
    },
  })

  const quantity = Number(draft.qty_delta)
  const ready =
    Boolean(draft.product_id && draft.location_id) && Number.isInteger(quantity) && quantity !== 0

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Correct stock"
      description="Type the change, not the new total. A large correction goes to a second person before anything moves."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => raise.mutate()} disabled={!ready} loading={raise.isPending}>
            Submit
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="error">{error}</Alert>}

        <Field label="Product">
          {(props) => (
            <select
              {...props}
              className={SELECT_CLASS}
              value={draft.product_id}
              onChange={(event) => setDraft({ ...draft, product_id: event.target.value })}
            >
              <option value="">Choose a product…</option>
              {(products.data ?? []).map((product) => (
                <option key={product.id} value={product.id}>
                  {product.code} · {product.name}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Location">
          {(props) => (
            <select
              {...props}
              className={SELECT_CLASS}
              value={draft.location_id}
              onChange={(event) => setDraft({ ...draft, location_id: event.target.value })}
            >
              <option value="">Choose a location…</option>
              {(locations.data?.data ?? []).map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field
          label="Change in units"
          hint={
            onHand === null
              ? 'Negative writes stock off, positive adds it. Zero is not a correction.'
              : `${onHand} on hand now. After this: ${onHand + (Number.isFinite(quantity) ? quantity : 0)}.`
          }
        >
          {(props) => (
            <Input
              {...props}
              type="number"
              step={1}
              placeholder="-12"
              value={draft.qty_delta}
              onChange={(event) => setDraft({ ...draft, qty_delta: event.target.value })}
            />
          )}
        </Field>

        <Field label="Reason">
          {(props) => (
            <select
              {...props}
              className={SELECT_CLASS}
              value={draft.reason}
              onChange={(event) =>
                setDraft({ ...draft, reason: event.target.value as AdjustmentReason })
              }
            >
              {ADJUSTMENT_REASONS.map((reason) => (
                <option key={reason} value={reason}>
                  {ADJUSTMENT_REASON_LABELS[reason]}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Note" hint="What happened. The approver has only this to go on.">
          {(props) => (
            <Input
              {...props}
              value={draft.notes}
              onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
              placeholder="Pallet crushed in the yard"
            />
          )}
        </Field>
      </div>
    </Modal>
  )
}

/**
 * The correction queue: every manual change to stock, and the ones still waiting.
 *
 * Small corrections appear here already approved rather than being hidden — the table is
 * the whole history of hand-written stock changes, which is the record an auditor asks
 * for, and a queue that only shows what is pending answers a much smaller question.
 */
export function StockAdjustments() {
  const [params, setParams] = useSearchParams()
  const [status, setStatus] = useState<AdjustmentStatus | ''>('pending')
  const [page, setPage] = useState(1)
  const [raising, setRaising] = useState(false)
  const [rejecting, setRejecting] = useState<Adjustment | null>(null)
  const [note, setNote] = useState('')
  const can = useAuth((s) => s.can)
  const queryClient = useQueryClient()

  const productId = params.get('product_id') ?? ''

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['adjustments', { status, page, productId }],
    queryFn: () => listAdjustments({ status, page, product_id: productId || undefined }),
    retry: false,
  })
  const products = useQuery({
    queryKey: ['products', 'all'],
    queryFn: () => listAllProducts(),
    retry: false,
    staleTime: 5 * 60_000,
  })
  const locations = useQuery({
    queryKey: ['stock-locations'],
    queryFn: listLocations,
    retry: false,
    staleTime: 5 * 60_000,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['adjustments'] })
    void queryClient.invalidateQueries({ queryKey: ['balances'] })
    void queryClient.invalidateQueries({ queryKey: ['movements'] })
    void queryClient.invalidateQueries({ queryKey: ['valuation'] })
  }

  const fail = (mutationError: unknown) =>
    toast.error(
      mutationError instanceof ApiError
        ? mutationError.message
        : 'Could not reach the server. Nothing was changed.',
    )

  const approve = useMutation({
    mutationFn: (adjustment: Adjustment) => approveAdjustment(adjustment.id),
    onSuccess: () => {
      refresh()
      toast.success('Approved. The stock has moved.')
    },
    onError: fail,
  })

  const reject = useMutation({
    mutationFn: (adjustment: Adjustment) => rejectAdjustment(adjustment.id, note),
    onSuccess: () => {
      refresh()
      toast.success('Rejected. The shelf is unchanged.')
      setRejecting(null)
      setNote('')
    },
    onError: (mutationError: unknown) => {
      fail(mutationError)
      setRejecting(null)
    },
  })

  const productOf = (id: string) => {
    const product = products.data?.find((candidate) => candidate.id === id)
    return product ? `${product.code} · ${product.name}` : '—'
  }
  const locationOf = (id: string) =>
    locations.data?.data.find((location) => location.id === id)?.name ?? '—'

  const canDecide = can('inventory', 'delete')

  const columns: Column<Adjustment>[] = [
    {
      key: 'created_at',
      header: 'Raised',
      className: 'whitespace-nowrap text-slate-500 dark:text-slate-400',
      sortValue: (row) => row.created_at,
      cell: (row) => formatWhen(row.created_at),
    },
    {
      key: 'product',
      header: 'Product',
      className: 'font-medium',
      sortValue: (row) => productOf(row.product_id),
      cell: (row) => productOf(row.product_id),
    },
    {
      key: 'location',
      header: 'Location',
      cell: (row) => locationOf(row.location_id),
    },
    {
      key: 'qty_delta',
      header: 'Change',
      className: 'text-right font-medium tabular-nums',
      headerClassName: 'text-right',
      sortValue: (row) => row.qty_delta,
      cell: (row) => (
        <span className={row.qty_delta < 0 ? 'text-danger' : 'text-success'}>
          {formatDelta(row.qty_delta)}
        </span>
      ),
    },
    {
      key: 'value',
      // What it was worth when it was raised, which is the figure the limit was applied
      // to. Re-pricing it at today's average would move the goalposts on the approver.
      header: 'Value',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      sortValue: (row) => Number(row.value),
      cell: (row) => formatMoney(row.value),
    },
    {
      key: 'reason',
      header: 'Reason',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (row) => (
        <span>
          {ADJUSTMENT_REASON_LABELS[row.reason] ?? row.reason}
          {row.notes && <span className="block text-xs">{row.notes}</span>}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_TONE[row.status]}`}>
          {ADJUSTMENT_STATUS_LABELS[row.status]}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      headerClassName: 'text-right',
      className: 'whitespace-nowrap text-right',
      cell: (row) =>
        row.status === 'pending' && canDecide ? (
          <span className="inline-flex gap-1">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => approve.mutate(row)}
              loading={approve.isPending && approve.variables?.id === row.id}
            >
              Approve
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRejecting(row)}>
              Reject
            </Button>
          </span>
        ) : null,
    },
  ]

  const meta = data?.meta

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Stock corrections</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Every hand-written change to stock, and who agreed to it.
          </p>
        </div>
        {can('inventory', 'update') && (
          <Button onClick={() => setRaising(true)}>Correct stock</Button>
        )}
      </header>

      {productId && (
        <Alert tone="info" title="Filtered to one product">
          Showing corrections for {productOf(productId)}.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => {
              params.delete('product_id')
              setParams(params, { replace: true })
            }}
          >
            Show all
          </button>
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Queue"
          description="Nobody approves their own correction, so a pending line needs a second pair of eyes before the stock moves."
        />
        <DataTable
          rows={data?.data ?? []}
          columns={columns}
          getRowId={(row) => row.id}
          caption="Stock corrections"
          isLoading={isLoading}
          error={error}
          emptyMessage={
            status === 'pending' ? 'Nothing is waiting for approval.' : 'No corrections to show.'
          }
          toolbar={
            <div className="flex items-center gap-2">
              <label className="sr-only" htmlFor="adjustment-status">
                Status
              </label>
              <select
                id="adjustment-status"
                className={SELECT_CLASS}
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value as AdjustmentStatus | '')
                  setPage(1)
                }}
              >
                <option value="pending">Awaiting approval</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="">Everything</option>
              </select>
            </div>
          }
          pagination={
            meta
              ? { page: meta.page, totalPages: meta.total_pages, onPage: setPage, isFetching }
              : undefined
          }
        />
      </Card>

      <RaiseDialog open={raising} onClose={() => setRaising(false)} initialProductId={productId} />

      <Modal
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        title="Reject this correction"
        description="The stock stays as it is. The line stays on the record with your reason against it."
        footer={
          <>
            <Button variant="secondary" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => rejecting && reject.mutate(rejecting)}
              loading={reject.isPending}
            >
              Reject
            </Button>
          </>
        }
      >
        <Field label="Why" hint="Read by whoever raised it.">
          {(props) => (
            <Input
              {...props}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Count it again with a supervisor"
            />
          )}
        </Field>
      </Modal>
    </div>
  )
}
